import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const PromptGenerator = ({ onPromptGenerated, onDataChanged, initialPrompt, initialData }) => {
  const { FiCheck, FiEdit, FiRefreshCw, FiSend } = FiIcons;
  
  const [formData, setFormData] = useState({
    location: 'Santorini',
    vibe: 'Cultural Immersion',
    travelerType: 'Solo',
    ageGroup: 'Adult (25-64)',
    gender: 'Non-binary',
    lgbtqIdentity: 'Ally',
    mobility: 'None',
    accessibility: 'Standard Access',
    experience: 'Local Market Exploration',
    pose: 'Engaged with local culture',
    culturalContext: 'Respectful Tourism'
  });
  
  const [outputType, setOutputType] = useState('static');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  
  // Initialize with initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
    if (initialPrompt) {
      setGeneratedPrompt(initialPrompt);
    }
  }, [initialData, initialPrompt]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOutputTypeChange = (type) => {
    setOutputType(type);
  };

  const generatePrompt = () => {
    const { 
      location, 
      vibe, 
      travelerType, 
      ageGroup, 
      gender, 
      lgbtqIdentity, 
      mobility, 
      accessibility, 
      experience, 
      pose, 
      culturalContext 
    } = formData;
    
    // Build traveler description
    let travelerDescription = '';
    
    // Age and traveler type
    if (travelerType === 'Family with Children') {
      travelerDescription = `a multigenerational family`;
    } else if (travelerType === 'Couple') {
      travelerDescription = `a couple`;
    } else {
      travelerDescription = `${travelerType.toLowerCase()} travelers`;
    }
    
    // Age group
    if (ageGroup !== 'Adult (25-64)') {
      travelerDescription += ` (${ageGroup})`;
    }
    
    // Gender identity
    if (gender !== 'Not Specified') {
      travelerDescription += ` identifying as ${gender.toLowerCase()}`;
    }
    
    // LGBTQ+ identity
    if (lgbtqIdentity === 'LGBTQ+ Traveler') {
      travelerDescription += ` and proudly LGBTQ+`;
    } else if (lgbtqIdentity === 'LGBTQ+ Family') {
      travelerDescription += ` as an LGBTQ+ family`;
    }
    
    // Mobility and accessibility
    let accessibilityDescription = '';
    if (mobility !== 'None') {
      accessibilityDescription = `, using ${mobility.toLowerCase()}`;
    }
    if (accessibility !== 'Standard Access') {
      accessibilityDescription += ` with ${accessibility.toLowerCase()}`;
    }
    
    // Cultural context
    let culturalNote = '';
    if (culturalContext === 'Respectful Tourism') {
      culturalNote = 'approached with cultural sensitivity and respect';
    } else if (culturalContext === 'Educational Focus') {
      culturalNote = 'with focus on learning and education';
    } else if (culturalContext === 'Community Engagement') {
      culturalNote = 'engaging authentically with local communities';
    }
    
    const prompt = `Experience inclusive travel through the lens of ${travelerDescription}${accessibilityDescription}, captured ${pose.toLowerCase()} during ${experience.toLowerCase()} in ${location}. This ${vibe.toLowerCase()} journey embodies authentic global citizenship and ${culturalNote}, showcasing how diverse travelers can explore the world safely and joyfully.`;
    
    return prompt;
  };

  const handleGeneratePrompt = () => {
    const newPrompt = generatePrompt();
    setGeneratedPrompt(newPrompt);
    console.log('Generating new prompt:', newPrompt);
    onPromptGenerated(newPrompt);
    onDataChanged({...formData, outputType});
  };

  const handleRandomize = () => {
    const options = {
      location: [
        'Santorini', 'Kyoto', 'Machu Picchu', 'Dubai', 'Bali', 
        'Iceland', 'Morocco', 'New Zealand', 'Peru', 'Egypt',
        'Thailand', 'Norway', 'Japan', 'Italy', 'Costa Rica'
      ],
      vibe: [
        'Cultural Immersion', 'Adventure Travel', 'Luxury Escape', 
        'Wellness Retreat', 'Historical Journey', 'Nature Discovery',
        'Urban Exploration', 'Beach Paradise', 'Mountain Trek'
      ],
      travelerType: [
        'Solo', 'Couple', 'Family with Children', 'Group of Friends', 
        'Multi-generational Family', 'Business Traveler', 'Digital Nomad'
      ],
      ageGroup: [
        'Young Adult (18-24)', 'Adult (25-64)', 'Senior (65+)', 
        'Teen (13-17)', 'Child (5-12)', 'All Ages'
      ],
      gender: [
        'Not Specified', 'Male', 'Female', 'Non-binary', 
        'Transgender', 'Genderfluid', 'Two-Spirit'
      ],
      lgbtqIdentity: [
        'Ally', 'LGBTQ+ Traveler', 'LGBTQ+ Family', 'LGBTQ+ Couple'
      ],
      mobility: [
        'None', 'Wheelchair', 'Walker', 'Cane', 'Scooter', 
        'Service Animal', 'Prosthetic Device'
      ],
      accessibility: [
        'Standard Access', 'Visual Assistance', 'Hearing Support', 
        'Cognitive Accessibility', 'Sensory-Friendly', 'Neurodivergent Support'
      ],
      experience: [
        'Local Market Exploration', 'Historical Site Visit', 
        'Natural Wonder Discovery', 'Cultural Festival Participation',
        'Culinary Experience', 'Adventure Activity', 'Relaxation Moment'
      ],
      pose: [
        'Engaged with local culture', 'Admiring scenery', 
        'Interacting with locals', 'Reflective moment', 
        'Active participation', 'Joyful expression'
      ],
      culturalContext: [
        'Respectful Tourism', 'Educational Focus', 'Community Engagement',
        'Sustainable Travel', 'Authentic Experience'
      ]
    };
    
    const randomSelect = (options) => {
      return options[Math.floor(Math.random() * options.length)];
    };
    
    const randomData = {
      location: randomSelect(options.location),
      vibe: randomSelect(options.vibe),
      travelerType: randomSelect(options.travelerType),
      ageGroup: randomSelect(options.ageGroup),
      gender: randomSelect(options.gender),
      lgbtqIdentity: randomSelect(options.lgbtqIdentity),
      mobility: randomSelect(options.mobility),
      accessibility: randomSelect(options.accessibility),
      experience: randomSelect(options.experience),
      pose: randomSelect(options.pose),
      culturalContext: randomSelect(options.culturalContext)
    };
    
    setFormData(randomData);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-2xl p-6 shadow-xl">
        <h2 className="text-3xl font-bold mb-6 text-yellow-400">Inclusive Travel Prompt Generator</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-1">Destination</label>
              <select 
                name="location" 
                value={formData.location} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Santorini">Santorini, Greece</option>
                <option value="Kyoto">Kyoto, Japan</option>
                <option value="Machu Picchu">Machu Picchu, Peru</option>
                <option value="Dubai">Dubai, UAE</option>
                <option value="Bali">Bali, Indonesia</option>
                <option value="Iceland">Iceland</option>
                <option value="Morocco">Morocco</option>
                <option value="New Zealand">New Zealand</option>
                <option value="Thailand">Thailand</option>
                <option value="Norway">Norway</option>
                <option value="Italy">Italy</option>
                <option value="Costa Rica">Costa Rica</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">Travel Vibe</label>
              <select 
                name="vibe" 
                value={formData.vibe} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Cultural Immersion">Cultural Immersion</option>
                <option value="Adventure Travel">Adventure Travel</option>
                <option value="Luxury Escape">Luxury Escape</option>
                <option value="Wellness Retreat">Wellness Retreat</option>
                <option value="Historical Journey">Historical Journey</option>
                <option value="Nature Discovery">Nature Discovery</option>
                <option value="Urban Exploration">Urban Exploration</option>
                <option value="Beach Paradise">Beach Paradise</option>
                <option value="Mountain Trek">Mountain Trek</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">Traveler Type</label>
              <select 
                name="travelerType" 
                value={formData.travelerType} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Solo">Solo Traveler</option>
                <option value="Couple">Couple</option>
                <option value="Family with Children">Family with Children</option>
                <option value="Group of Friends">Group of Friends</option>
                <option value="Multi-generational Family">Multi-generational Family</option>
                <option value="Business Traveler">Business Traveler</option>
                <option value="Digital Nomad">Digital Nomad</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">Age Group</label>
              <select 
                name="ageGroup" 
                value={formData.ageGroup} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Young Adult (18-24)">Young Adult (18-24)</option>
                <option value="Adult (25-64)">Adult (25-64)</option>
                <option value="Senior (65+)">Senior (65+)</option>
                <option value="Teen (13-17)">Teen (13-17)</option>
                <option value="Child (5-12)">Child (5-12)</option>
                <option value="All Ages">All Ages</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">Gender Identity</label>
              <select 
                name="gender" 
                value={formData.gender} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Not Specified">Not Specified</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Non-binary">Non-binary</option>
                <option value="Transgender">Transgender</option>
                <option value="Genderfluid">Genderfluid</option>
                <option value="Two-Spirit">Two-Spirit</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">LGBTQ+ Identity</label>
              <select 
                name="lgbtqIdentity" 
                value={formData.lgbtqIdentity} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Ally">Ally</option>
                <option value="LGBTQ+ Traveler">LGBTQ+ Traveler</option>
                <option value="LGBTQ+ Family">LGBTQ+ Family</option>
                <option value="LGBTQ+ Couple">LGBTQ+ Couple</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-1">Mobility Support</label>
              <select 
                name="mobility" 
                value={formData.mobility} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="None">None</option>
                <option value="Wheelchair">Wheelchair</option>
                <option value="Walker">Walker</option>
                <option value="Cane">Cane</option>
                <option value="Scooter">Scooter</option>
                <option value="Service Animal">Service Animal</option>
                <option value="Prosthetic Device">Prosthetic Device</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">Accessibility Needs</label>
              <select 
                name="accessibility" 
                value={formData.accessibility} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Standard Access">Standard Access</option>
                <option value="Visual Assistance">Visual Assistance</option>
                <option value="Hearing Support">Hearing Support</option>
                <option value="Cognitive Accessibility">Cognitive Accessibility</option>
                <option value="Sensory-Friendly">Sensory-Friendly</option>
                <option value="Neurodivergent Support">Neurodivergent Support</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">Experience Type</label>
              <select 
                name="experience" 
                value={formData.experience} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Local Market Exploration">Local Market Exploration</option>
                <option value="Historical Site Visit">Historical Site Visit</option>
                <option value="Natural Wonder Discovery">Natural Wonder Discovery</option>
                <option value="Cultural Festival Participation">Cultural Festival Participation</option>
                <option value="Culinary Experience">Culinary Experience</option>
                <option value="Adventure Activity">Adventure Activity</option>
                <option value="Relaxation Moment">Relaxation Moment</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">Activity Pose</label>
              <select 
                name="pose" 
                value={formData.pose} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Engaged with local culture">Engaged with local culture</option>
                <option value="Admiring scenery">Admiring scenery</option>
                <option value="Interacting with locals">Interacting with locals</option>
                <option value="Reflective moment">Reflective moment</option>
                <option value="Active participation">Active participation</option>
                <option value="Joyful expression">Joyful expression</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-1">Cultural Approach</label>
              <select 
                name="culturalContext" 
                value={formData.culturalContext} 
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="Respectful Tourism">Respectful Tourism</option>
                <option value="Educational Focus">Educational Focus</option>
                <option value="Community Engagement">Community Engagement</option>
                <option value="Sustainable Travel">Sustainable Travel</option>
                <option value="Authentic Experience">Authentic Experience</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4 text-gray-300">Output Type</h3>
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={() => handleOutputTypeChange('static')}
              className={`px-4 py-2 rounded-lg ${outputType === 'static' ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              Static Image
            </button>
            <button 
              onClick={() => handleOutputTypeChange('video')}
              className={`px-4 py-2 rounded-lg ${outputType === 'video' ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              Video Content
            </button>
            <button 
              onClick={() => handleOutputTypeChange('carousel')}
              className={`px-4 py-2 rounded-lg ${outputType === 'carousel' ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              Carousel Set
            </button>
          </div>
        </div>
        
        <div className="flex justify-between mb-8">
          <button 
            onClick={handleRandomize}
            className="bg-purple-500 hover:bg-purple-400 text-white px-6 py-3 rounded-lg flex items-center space-x-2"
          >
            <SafeIcon icon={FiRefreshCw} />
            <span>Randomize</span>
          </button>
          
          <button 
            onClick={handleGeneratePrompt}
            className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-3 rounded-lg flex items-center space-x-2"
          >
            <SafeIcon icon={FiSend} />
            <span>Generate Prompt</span>
          </button>
        </div>
        
        {generatedPrompt && (
          <div className="bg-gray-700 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-white flex items-center">
              <SafeIcon icon={FiEdit} className="mr-2 text-yellow-400" />
              Generated Prompt
            </h3>
            <p className="text-gray-300 whitespace-pre-line">{generatedPrompt}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptGenerator;